<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

import BaseBottomSheet from '@/components/ui/BaseBottomSheet.vue'
import { useBottomSheet } from '@/composables/useBottomSheet'
import { useEventListener } from '@/composables/useEventListener'
import { FLOATING_SHEET_BOTTOM_OFFSET, mobileSheetMaxHeight } from '@/services/safeAreaService'
import { Z_INDEX } from '@/utils/zIndex'

defineOptions({ name: 'ProfileSheet' })

// Mobile account sheet. Shares the floating-drawer language of CanvasSheet / the Collection
// drawer (inset left-3/right-3, rounded top, drag handle, sits above the tab bar) but is sized
// to its content — it is a finite menu, not a persistent peek/mid/full surface. Two snaps:
// `open` (content height, capped) and `peek` (collapsed header) for drag-down.
// The measured element is the inner content wrapper inside the sheet body slot.
const contentRef = ref<HTMLElement | null>(null)

const viewportHeight = ref(typeof window === 'undefined' ? 844 : window.innerHeight)
const contentHeight = ref(360)

// Fixed chrome above the scrollable body: drag handle (py-1.5 + 6px pill) + header (min-h-10).
const CHROME_HEIGHT = 24 + 40
const MIN_OPEN_HEIGHT = 200

const sheetHeights = computed<Record<'peek' | 'open', number>>(() => {
  const fullCap = viewportHeight.value - 88
  const open = Math.max(MIN_OPEN_HEIGHT, Math.min(contentHeight.value + CHROME_HEIGHT, fullCap))
  return { peek: 58, open }
})

const sheet = useBottomSheet({
  modes: ['peek', 'open'] as const,
  heights: sheetHeights,
  initialMode: 'open',
})

const sheetStyle = computed(() => ({
  height: `${sheet.height.value}px`,
  maxHeight: mobileSheetMaxHeight(FLOATING_SHEET_BOTTOM_OFFSET),
  bottom: FLOATING_SHEET_BOTTOM_OFFSET,
  zIndex: String(Z_INDEX.MOBILE_TOOLBAR),
  transition: sheet.isDragging.value ? 'none' : 'height 180ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
}))

useEventListener(window, 'resize', () => {
  viewportHeight.value = window.innerHeight
})

// Keep `open` height matched to the live content (e.g. when the Legal sub-section expands).
let resizeObserver: ResizeObserver | null = null
const measureContent = () => {
  // Ignore zero readings (body hidden at peek) so the measured height survives a drag-down.
  const measured = contentRef.value?.scrollHeight ?? 0
  if (measured > 0) contentHeight.value = measured
}
onMounted(() => {
  measureContent()
  if (typeof ResizeObserver !== 'undefined' && contentRef.value) {
    resizeObserver = new ResizeObserver(measureContent)
    resizeObserver.observe(contentRef.value)
  }
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  resizeObserver = null
})

const showBody = computed(() => sheet.mode.value !== 'peek')
</script>

<template>
  <BaseBottomSheet
    :sheet="sheet"
    title="Account"
    :sheet-style="sheetStyle"
    :show-body="showBody"
    testid="profile-sheet"
    role="menu"
    aria-label="Account"
  >
    <!-- Body: the account menu -->
    <div ref="contentRef" class="pb-2">
      <slot />
    </div>
  </BaseBottomSheet>
</template>
